/**
 * Campaign Configuration System
 *
 * Template Encoding: [boundary]-[experience]-[regType]-[rewardType]
 * - Boundary: instant, geo
 * - Experience: ar, map, quiz, survey
 * - Registration type: 1 (name/phone/T&C/marketing) — more in future
 * - Reward type: 1 (video+voucher) — more in future
 *
 * See Confluence: Campaign Templates 2.0 > Serial Construction
 */

import type {
  CampaignTemplate,
  CampaignBoundary,
  CampaignExperience,
  CampaignFeature,
  CampaignService,
  CampaignTier,
  BoundaryOption,
  ExperienceOption,
  FeatureOption,
  ServiceOption
} from '@/types/campaign.types'
import { buildApprovedAssetUrls } from '@/config/assetBuckets'
import { buildQuizConfigFromTemplate } from '@/config/quiz-templates.config'

// Re-export types for convenience
export type { CampaignTemplate, CampaignBoundary, CampaignExperience, CampaignFeature, CampaignService, CampaignTier } from '@/types/campaign.types'

/**
 * Generate unique backend template ID for each of the 36 configurations
 * Format: irl-template-{ServiceNumber}{FeatureLetter}-{tier}
 * 
 * Examples:
 * - Freemium + Tap to Redeem + Map Only → irl-template-001a-freemium
 * - Premium + Voucher + AR Only → irl-template-002b-premium
 * - Enterprise + QR Code + AR+Map → irl-template-004c-enterprise
 */
function generateBackendTemplateId(
  tier: CampaignTier,
  service: CampaignService,
  feature: CampaignFeature
): string {
  const serviceNumber = {
    'tap-to-redeem': '001',
    'voucher': '002',
  }[service]
  
  const featureLetter = {
    'instant-ar': 'b',
    'location-ar': 'c',
    'quiz': 'd'
  }[feature]

  return `irl-template-${serviceNumber}${featureLetter}-${tier}`
}

import arScreen1 from '@/assets/Campaign Creator Assets/ar-screen-1.png'
import gpsScreen1 from '@/assets/Campaign Creator Assets/gps-screen-1.png'
import { platformIcon } from '@/config/platformAssets'

/**
 * Build the campaign business template serial ID
 * Format: [boundary]-[experience]-[regType]-[rewardType]
 */
export function buildCampaignBusinessTemplate(
  boundary: CampaignBoundary,
  experience: CampaignExperience,
  regType: number = 1,
  rewardType: number = 1
): string {
  return `${boundary}-${experience}-${regType}-${rewardType}`
}

/**
 * Boundary options for UI display (step 1)
 */
export const BOUNDARY_OPTIONS: BoundaryOption[] = [
  {
    id: 'instant',
    label: 'Anyone,\neverywhere',
    description: 'No specific location required',
    icon: platformIcon('star.png'),
    color: '#96ffc9',
    image: ''
  },
  {
    id: 'geo',
    label: 'Specific\nLocations',
    description: 'Played at locations you specify',
    icon: platformIcon('pin.png'),
    color: '#96ffc9',
    image: ''
  }
]

/**
 * Experience options for UI display (step 2)
 */
export const EXPERIENCE_OPTIONS: ExperienceOption[] = [
  {
    id: 'quiz',
    label: 'Quiz',
    description: 'Build quiz pages to reward players',
    icon: '',
    color: '#96ffc9'
  },
  {
    id: 'survey',
    label: 'Survey',
    description: 'Gamified survey for collecting data',
    icon: '',
    color: '#96ffc9'
  },
  {
    id: 'ar',
    label: 'AR',
    description: 'Drop prize objects in AR mode',
    icon: '',
    color: '#96ffc9'
  }
]

/**
 * Default IR-branded campaign configuration.
 *
 * Change `DEFAULT_BOUNDARY` and `DEFAULT_EXPERIENCE` to switch the
 * out-of-the-box template — everything else derives from these two.
 */
const DEFAULT_BOUNDARY: CampaignBoundary = 'instant'
const DEFAULT_EXPERIENCE: CampaignExperience = 'quiz'

const defaultBackgroundUrls = buildApprovedAssetUrls('defaults/backgrounds/mint-circle-pattern.png')
const defaultLogoUrls = buildApprovedAssetUrls('defaults/logos/irl-text-default.png')

const DEFAULT_WIN_MESSAGES: Record<CampaignExperience, string> = {
  quiz: 'Well done! You passed the quiz',
  survey: 'Thanks for your feedback!',
  ar: 'You found it! Claim your reward',
  map: 'You found it! Claim your reward',
}

export const IR_DEFAULT_CONFIG = {
  boundary: DEFAULT_BOUNDARY,
  experience: DEFAULT_EXPERIENCE,
  campaignBusinessTemplate: buildCampaignBusinessTemplate(DEFAULT_BOUNDARY, DEFAULT_EXPERIENCE),

  message: DEFAULT_WIN_MESSAGES[DEFAULT_EXPERIENCE],

  quizConfig: buildQuizConfigFromTemplate(),

  lookFeel: {
    brandColors: {
      primary: '#ffffff',
      secondary: '#7ae6b1',
      background: '#292e50',
      header: '#181b2f',
      buttonText: '#ffffff',
    },
    googleFont: 'Montserrat',
    selectedDefaultAssets: {
      logo: 'default-logo-irl-text-default' as string | null,
      mapMarker: null as string | null,
      ar: null as string | null,
      // Picked from the picker's file list so step 2 of resolveBackgroundPreviewUrl
      // always resolves to a real thumbnail URL on first load.
      registrationBackground: 'default-background-mint-circle-pattern' as string | null,
      prizeBackground: 'default-background-mint-circle-pattern' as string | null,
    },
    preBuiltUploadedAssets: {
      logo: {
        s3Url: defaultLogoUrls.httpUrl,
        s3Path: defaultLogoUrls.s3Path,
        fileName: 'irl-text-default.png',
        bucketType: 'approved' as const,
        fileSize: 0,
        fileType: '',
        uploadTimestamp: new Date(),
      },
      registrationBackground: {
        s3Url: defaultBackgroundUrls.httpUrl,
        s3Path: defaultBackgroundUrls.s3Path,
        fileName: 'mint-circle-pattern.png',
        bucketType: 'approved' as const,
        fileSize: 0,
        fileType: '',
        uploadTimestamp: new Date(),
      },
      prizeBackground: {
        s3Url: defaultBackgroundUrls.httpUrl,
        s3Path: defaultBackgroundUrls.s3Path,
        fileName: 'mint-circle-pattern.png',
        bucketType: 'approved' as const,
        fileSize: 0,
        fileType: '',
        uploadTimestamp: new Date(),
      },
    },
  },

  drops: {
    dropsPerDrop: 1,
    maxClaimsPerUser: 3,
    timePeriod: 24,
    winRatio: 50,
  },
} as const

/** Win message for a given experience type */
export function getDefaultWinMessage(experience: CampaignExperience): string {
  return DEFAULT_WIN_MESSAGES[experience]
}

export function isDefaultWinMessage(message: string): boolean {
  return Object.values(DEFAULT_WIN_MESSAGES).includes(message.trim())
}

/**
 * Feature options for UI display
 * @deprecated Use BOUNDARY_OPTIONS + EXPERIENCE_OPTIONS instead
 */
export const FEATURE_OPTIONS: FeatureOption[] = [
  {
    id: 'instant-ar',
    label: 'Instant',
    description: 'Offer\na virtual object',
    icon: '',
    color: '#52C696',
    image: arScreen1
  },
  {
    id: 'location-ar',
    label: 'Location-Based',
    description: 'Geofence\na virtual object',
    icon: '',
    color: '#66D9A7',
    image: gpsScreen1
  },
  {
    id: 'quiz',
    label: 'Quiz',
    description: 'Gate rewards\nwith a quiz',
    icon: '🧠',
    color: '#5ED4A0',
    image: arScreen1
  }
]

/**
 * Service options for UI display
 */
export const SERVICE_OPTIONS: ServiceOption[] = [
  {
    id: 'tap-to-redeem',
    label: 'Tap to collect',
    description: 'Users instantly claim their reward in one tap.',
    icon: '👆',
    color: '#95FEC9'
  },
  {
    id: 'voucher',
    label: 'Voucher Code',
    description: 'Users receive a voucher code they can redeem later',
    icon: '🎟️',
    color: '#7AECB8'
  }
]

/**
 * Helper function to generate template ID
 */
function generateTemplateId(
  tier: CampaignTier, 
  service: CampaignService, 
  feature: CampaignFeature
): string {
  const serviceNumber = {
    'tap-to-redeem': '001',
    'voucher': '002',
  }[service]
  
  const featureLetter = {
    'instant-ar': 'b',
    'location-ar': 'c',
    'quiz': 'd'
  }[feature]

  const tierPrefix = tier.charAt(0).toUpperCase() + tier.slice(1)
  
  return `${tierPrefix}_${serviceNumber}${featureLetter}`
}

/**
 * Helper function to get display name
 */
function getDisplayName(feature: CampaignFeature, service: CampaignService): string {
  const featureLabel = FEATURE_OPTIONS.find(f => f.id === feature)?.label || feature
  const serviceLabel = SERVICE_OPTIONS.find(s => s.id === service)?.label || service
  return `${featureLabel} - ${serviceLabel}`
}

/**
 * Helper function to get description
 */
function getDescription(tier: CampaignTier, feature: CampaignFeature, service: CampaignService): string {
  const featureDesc = FEATURE_OPTIONS.find(f => f.id === feature)?.description || ''
  const serviceDesc = SERVICE_OPTIONS.find(s => s.id === service)?.description || ''
  return `${tier.toUpperCase()}: ${featureDesc} with ${serviceDesc}`
}

/**
 * Generate all templates for all tiers
 */
function generateAllTemplates(): CampaignTemplate[] {
  const templates: CampaignTemplate[] = []
  
  const tiers: Array<{ tier: CampaignTier; tokens: Record<CampaignFeature, number>; features: string[] }> = [
    {
      tier: 'freemium',
      tokens: { 'instant-ar': 10, 'location-ar': 5, 'quiz': 5 },
      features: []
    },
    {
      tier: 'premium',
      tokens: { 'instant-ar': 100, 'location-ar': 100, 'quiz': 100 },
      features: ['Basic Reporting', 'Per Claim Notification', 'Info Page']
    },
    {
      tier: 'enterprise',
      tokens: { 'instant-ar': 10, 'location-ar': 5, 'quiz': 10 },
      features: [
        'Custom Data Reporting',
        'Webhook Integration',
        'Advanced Analytics',
        'Custom Voucher Options',
        'Priority Support',
        'Full API Access',
        'Custom AR Templates',
        'Geo-Fencing'
      ]
    }
  ]
  
  const features: CampaignFeature[] = ['instant-ar', 'location-ar', 'quiz']
  const services: CampaignService[] = ['tap-to-redeem', 'voucher']
  
  for (const tierConfig of tiers) {
    for (const service of services) {
      for (const feature of features) {
        templates.push({
          id: generateTemplateId(tierConfig.tier, service, feature),
          tier: tierConfig.tier,
          feature,
          service,
          tokens: tierConfig.tokens[feature],
          features: tierConfig.features,
          campaignBusinessTemplate: generateBackendTemplateId(tierConfig.tier, service, feature),
          displayName: getDisplayName(feature, service),
          description: getDescription(tierConfig.tier, feature, service)
        })
      }
    }
  }
  
  return templates
}

/**
 * All 36 campaign templates
 */
export const ALL_TEMPLATES: CampaignTemplate[] = generateAllTemplates()

/**
 * Get templates filtered by tier
 */
export function getTemplatesByTier(tier: CampaignTier): CampaignTemplate[] {
  return ALL_TEMPLATES.filter(template => template.tier === tier)
}

/**
 * Get a specific template by feature and service for a given tier
 */
export function getTemplate(
  tier: CampaignTier,
  feature: CampaignFeature,
  service: CampaignService
): CampaignTemplate | undefined {
  return ALL_TEMPLATES.find(
    template => 
      template.tier === tier && 
      template.feature === feature && 
      template.service === service
  )
}

/**
 * Get template by ID
 */
export function getTemplateById(id: string): CampaignTemplate | undefined {
  return ALL_TEMPLATES.find(template => template.id === id)
}

/**
 * Get all available features for a tier
 */
export function getAvailableFeatures(tier: CampaignTier): FeatureOption[] {
  // All tiers have access to all features
  return FEATURE_OPTIONS
}

/**
 * Get all available services for a tier
 */
export function getAvailableServices(tier: CampaignTier): ServiceOption[] {
  // All tiers have access to all services
  return SERVICE_OPTIONS
}

/**
 * Convert feature ID to human-readable format for backend compatibility
 */
export function featureToExperienceType(feature: CampaignFeature): string {
  const map: Record<CampaignFeature, string> = {
    'instant-ar': 'Augmented Reality',
    'location-ar': 'Augmented Reality AND Map',
    'quiz': 'Quiz'
  }
  return map[feature]
}

/**
 * Convert service ID to human-readable format for backend compatibility
 */
export function serviceToRedeemType(service: CampaignService): string {
  const map: Record<CampaignService, string> = {
    'tap-to-redeem': 'Tap to collect',
    'voucher': 'Voucher Code',
  }
  return map[service]
}
